import globals from "@lvt/globals";
import {
    Alert as StreamAlert,
    ArrowLottie,
    CameraStream,
    CameraStreamContext,
    cameraStreamServiceImpl,
    CheckInResponse,
    ClickToCenter,
    ControlService,
    controlServiceImpl,
    FullScreen,
    PanTilt,
    PanTiltVal,
    PresetsButtonV2,
    SnapshotButtonV2,
    StartStreamResponse,
    StreamBanner,
    StreamingStatus,
    Zoom,
    ZoomInLottie,
    ZoomOutLottie,
    ZoomVal,
} from "@lvt/streaming";
import { Alert } from "@material-ui/lab";
import { CameraAlt } from "@mui/icons-material";
import clsx from "clsx";
import DetectRTC from "detectrtc";
import Lottie, { LottieRef, LottieRefCurrentProps } from "lottie-react";
import { useSnackbar } from "notistack";
import { FC, MutableRefObject, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import client from "@/api/client";
import { useAddPresetMutation, useUpdatePresetMutation } from "@/api/liveUnitMosaic/liveUnitMosaic";
import { Presets } from "@/common/components/SVG/Svg";
import { MosaicItemDimensionsContext } from "@/common/components/liveUnitMosaic/Mosaic/MosaicItem/MosaicItem";
import { Loading } from "@/common/containers/Loading";
import { useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import CameraStreamData from "@/utilities/types/cameraStreamData";
import { CenterAnimation } from "@/utilities/types/streaming";

import useStyles from "./CameraStreamTile.styles";


interface CameraTileProps {
    datum: CameraStreamData;

    /**
     * Flag to hide the camera controls from the tile
     */
    hideControls: boolean;
}

const CameraStreamTile: FC<CameraTileProps> = ({ datum, hideControls }) => {
    const { cameraId, cameraName, capabilities, presets, controlPresets, controlPtzf, liveUnitId } = datum;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [startStreamData, setStartStreamData] = useState<StartStreamResponse>();
    const [checkInData, setCheckInData] = useState<CheckInResponse>();
    const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>(StreamingStatus.Initial);
    const [fullScreen, setFullScreen] = useState(false);
    const [snapshot, setSnapshot] = useState<string>();
    const [showCenterAnimation, setShowCenterAnimation] = useState<CenterAnimation>();
    const [open, setOpen] = useState(false);

    const [addPreset] = useAddPresetMutation();
    const [updatePreset] = useUpdatePresetMutation();

    const [controlService, setControlService] = useState<ControlService>(controlServiceImpl(client, cameraId));
    const cameraStreamService = cameraStreamServiceImpl(client);

    const { t } = useTranslation("translation", { keyPrefix: "components.alerts.cameraStreamTile" });
    const dimensions = useContext(MosaicItemDimensionsContext);
    const videoPreference = useSelector((state) => (state.user.videoPreference == "html5" ? "html5" : "webrtc"));
    const userPermissions = useSelector((state) => state.user.permissions);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const styles = useStyles();
    const centerAnimationRef = useRef<LottieRef>(null) as MutableRefObject<LottieRefCurrentProps | null>;
    const divRef = useRef(null);
    const dispatch = useDispatch();

    const homePreset = presets.find(p => p.isHome) ?? presets[0];
    const usingHtml5Player = !capabilities?.webrtc || videoPreference == "html5" || !DetectRTC.isWebRTCSupported;

    const allowClickToCenter = controlPtzf && capabilities.pan && capabilities.clickToCenter;
    const allowControlPresets = controlPresets && (capabilities.presets || capabilities.position);
    const allowPanTilt = controlPtzf && capabilities.pan;
    const allowSelfService = !!userPermissions?.selfService;
    const allowSnapshot = controlPtzf && capabilities.snapshot;
    const allowZoom = controlPtzf && capabilities.zoom;

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Action handlers ////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const startStream = (cameraId: number) => {
        return cameraStreamService.startStream(cameraId)
            .then((data) => setStartStreamData(data));
    };

    const checkIn = (cameraId: number, streamRecordId: number) => {
        return cameraStreamService.checkIn(cameraId, streamRecordId)
            .then((res) => setCheckInData(res))
            .catch((error) => {
                onAlertMessage({
                    type: "checkInFailed",
                    level: "error",
                    message: t("errors.checkIn"),
                });

                throw error;
            });
    };

    const handlePresetsMenu = () => {
        setAnchorEl(divRef.current);
        setOpen(!open);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Event handlers /////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const onAlertMessage = (alert: StreamAlert) => {
        closeSnackbar(alert.type);

        const AlertNotification = (
            <Alert key={alert.type} severity={alert.level}>
                {`${cameraName}: ${alert.message}`}
            </Alert>
        );

        enqueueSnackbar(AlertNotification, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8000,
            key: alert.message,
            preventDuplicate: true,
            variant: alert.level,
        });
    };

    const onError = (e: any) => {
        console.log("Stream error", e);
    };

    const onStatusChange = (status: StreamingStatus) => {
        // If this check is not present, the onIdle wake up will use stale streaming data
        if (status === StreamingStatus.Idle) setStartStreamData(undefined);

        setStreamingStatus(status);
    };

    const handleSnapshot = () => {
        controlService
            .takeSnapshot()
            .then(response => {
                setSnapshot(JSON.parse(response.data).image);
            })
            .catch(error => {
                dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: `${t("errors.snapshot")}: ${error.message}`,
                    }),
                );
            });
    };

    const onClickHome = () => {
        if (!homePreset) return;

        setShowCenterAnimation(CenterAnimation.Home);

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.goToPreset(homePreset.id);
    };

    const onClickPreset = (presetId: number) => {
        setShowCenterAnimation(CenterAnimation.Home);

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.goToPreset(presetId);
    };

    const onClickPanTilt = (direction: PanTiltVal) => {
        switch (direction) {
            case PanTiltVal.Up:
                setShowCenterAnimation(CenterAnimation.Up);
                break;
            case PanTiltVal.Down:
                setShowCenterAnimation(CenterAnimation.Down);
                break;
            case PanTiltVal.Left:
                setShowCenterAnimation(CenterAnimation.Left);
                break;
            case PanTiltVal.Right:
                setShowCenterAnimation(CenterAnimation.Right);
                break;
        }

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.panTilt(direction);
    };

    const onClickZoom = (direction: ZoomVal) => {
        switch (direction) {
            case ZoomVal.In:
                setShowCenterAnimation(CenterAnimation.ZoomIn);
                break;
            case ZoomVal.Out:
                setShowCenterAnimation(CenterAnimation.ZoomOut);
                break;
        }

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.zoom(direction);
    };

    const onAddPreset = (name: string) => {
        return addPreset({
            liveUnitId,
            cameraId,
            name,
        }).catch(error => {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `${t("errors.addPreset")}: ${error.message}`,
                }),
            );
        });
    };

    const onUpdatePreset = (presetId: number, name: string) => {
        return updatePreset({
            liveUnitId,
            presetId,
            name,
        }).catch(error => {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `${t("errors.updatePreset")}: ${error.message}`,
                }),
            );
        });
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Helper functions ///////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const getCenterAnimation = () => {
        switch (showCenterAnimation) {
            case CenterAnimation.Up:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(styles.centerAnimation, styles.upArrow)}
                    />
                );

            case CenterAnimation.Down:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(styles.centerAnimation, styles.downArrow)}
                    />
                );

            case CenterAnimation.Left:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(styles.centerAnimation, styles.leftArrow)}
                    />
                );

            case CenterAnimation.Right:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(styles.centerAnimation, styles.rightArrow)}
                    />
                );

            case CenterAnimation.ZoomIn:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ZoomInLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={styles.centerAnimation}
                    />
                );

            case CenterAnimation.ZoomOut:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ZoomOutLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={styles.centerAnimation}
                    />
                );

            default:
                return null;
        }
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    ////////////////////////////////////// React hooks //////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    useEffect(() => {
        if (cameraId) {
            setControlService(controlServiceImpl(client, cameraId));
            setStreamingStatus(StreamingStatus.Initial);
            setStartStreamData(undefined);
        }
    }, [cameraId]);

    return (
        <CameraStream
            cameraId={cameraId}
            canWebRtc={capabilities.webrtc}
            startStream={startStream}
            startStreamData={startStreamData ?? null}
            videoPreference={usingHtml5Player ? "html5" : "webrtc"}
            onAlertMessage={onAlertMessage}
            onError={onError}
            dimensionsOptions={dimensions}
            onStatusChange={onStatusChange}
            checkIn={checkIn}
            checkInData={checkInData}
            checkOut={cameraStreamService.checkOut}
            inFullScreen={fullScreen}
        >
            <CameraStreamContext.Consumer>
                {ctx => (
                    <>
                        {streamingStatus === StreamingStatus.Started &&
                            <Loading />
                        }

                        <StreamBanner streamingStatus={streamingStatus} getTimeUntilIdle={ctx.getTimeUntilIdle} />

                        <div className={styles.fadeControls}>
                            {showCenterAnimation !== undefined && getCenterAnimation()}

                            {streamingStatus === StreamingStatus.Active &&
                                <>
                                    <FullScreen
                                        className={styles.fullscreen}
                                        fullScreenRef={ctx.containerRef}
                                        onFullScreenChange={isFullScreen => setFullScreen(isFullScreen)}
                                    />

                                    {!hideControls &&
                                        <>
                                            <div className={styles.ptz}>
                                                {allowPanTilt &&
                                                    <PanTilt
                                                        onClickHome={onClickHome}
                                                        onClickPanTilt={onClickPanTilt}
                                                        className={styles.panTilt}
                                                    />
                                                }

                                                {allowZoom &&
                                                    <Zoom onClickZoom={onClickZoom} />
                                                }
                                            </div>

                                            {allowClickToCenter &&
                                                <ClickToCenter
                                                    onClickToCenter={positioning => controlService.clickToCenter(positioning)}
                                                    dimensions={ctx.videoDimensions}
                                                />
                                            }

                                            <div className={styles.actionButtons} ref={divRef}>
                                                {allowSnapshot &&
                                                    <SnapshotButtonV2
                                                        icon={<CameraAlt />}
                                                        image={snapshot}
                                                        onClick={handleSnapshot}
                                                        popoverText={t("actionButtons.snapshot")}
                                                    />
                                                }

                                                {allowControlPresets &&
                                                    <PresetsButtonV2
                                                        advancedSettingsUrl={`${globals.servicePortal.url}/camera/${cameraId}`}
                                                        allowSelfService={allowSelfService}
                                                        icon={<Presets />}
                                                        localizationStrings={{
                                                            newPreset: t("actionButtons.presets.newPreset"),
                                                            settings: t("actionButtons.presets.settings"),
                                                            title: t("actionButtons.presets.title"),
                                                        }}
                                                        menuAnchor={anchorEl}
                                                        onAdd={onAddPreset}
                                                        onClick={handlePresetsMenu}
                                                        onClickPreset={onClickPreset}
                                                        onUpdate={onUpdatePreset}
                                                        open={open}
                                                        presets={presets}
                                                    />
                                                }
                                            </div>
                                        </>
                                    }
                                </>
                            }
                        </div>
                    </>
                )}
            </CameraStreamContext.Consumer>
        </CameraStream>
    );
};

export default CameraStreamTile;
